// SalesView.js - Optimized
import React, { useState, lazy, useEffect, Fragment, useCallback, useRef } from 'react';
import moment from 'moment';
import axios from 'axios';
import { DatePicker, Spin, Button, Tooltip, InputNumber, Radio } from 'antd';
import Layout from '../layout/Layout';

import { API } from '../config/config';
import { displayErrors, displayNotification } from '../config/display';
import { headers } from '../config/token';

const TableSales = lazy(() => import('../components/Sales/TableSales'));
const { RangePicker } = DatePicker;

const defaultData = {
  tableData: [],
  tableKeys: [],
}

const SalesView = props => {
  const defaultDate = day => moment().day(day).format("YYYY-MM-DD");
  const [dateRange, setDateRange] = useState([defaultDate(0), defaultDate(7)]);
  const [conversion, setConversion] = useState(50);
  const [summaryFormat, setSummaryFormat] = useState("none");
  const [data, setData] = useState(defaultData);
  const [spin, setSpin] = useState(false);
  const abortControllerRef = useRef(null);
  const cacheRef = useRef(new Map());

  // Create cache key from params
  const getCacheKey = useCallback(() => {
    return `sales_${dateRange[0]}_${dateRange[1]}_${conversion}_${summaryFormat}`;
  }, [dateRange, conversion, summaryFormat]);

  const fetchReport = useCallback(async () => {
    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    
    const cacheKey = getCacheKey();
    
    // Check cache first
    if (cacheRef.current.has(cacheKey)) {
      const cachedData = cacheRef.current.get(cacheKey);
      setData(cachedData);
      return;
    }
    
    setSpin(true);
    
    const params = `from=${dateRange[0]}&to=${dateRange[1]}&conversion=${conversion}&summary=${summaryFormat}`;
    
    try {
      const res = await axios.get(API + `cposms/po/reporting/sales?${params}`, {
        ...headers(),
        signal: abortController.signal,
        timeout: 30000 // 30 second timeout
      });
      
      const { tableData, tableKeys } = res.data;
      const newData = { tableData, tableKeys };
      
      // Store in cache (limit cache size to 50 items)
      if (cacheRef.current.size > 50) {
        const firstKey = cacheRef.current.keys().next().value;
        cacheRef.current.delete(firstKey);
      }
      cacheRef.current.set(cacheKey, newData);
      
      setData(newData);
    } catch (err) {
      if (err.name !== 'AbortError' && err.name !== 'CanceledError') {
        displayErrors(err);
      }
    } finally {
      if (!abortController.signal.aborted) {
        setSpin(false);
      }
    }
  }, [dateRange, conversion, summaryFormat, getCacheKey]);

  const exportSalesCSV = useCallback(async () => {
    setSpin(true);
    
    try {
      const res = await axios({
        url: `${API}cposms/po/reporting/export-sales/dl?from=${dateRange[0]}&to=${dateRange[1]}&conversion=${conversion}`,
        method: 'get',
        responseType: 'blob',
        timeout: 60000, // 60 second timeout for export
        ...headers()
      });
      
      displayNotification('success', 'File successfully generated');
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('target', '_blank');
      link.setAttribute('download', `Sales_report_from_${dateRange[0]}_to_${dateRange[1]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentElement.removeChild(link);
      window.URL.revokeObjectURL(url); // Clean up
    } catch (err) {
      displayErrors(err);
    } finally {
      setSpin(false);
    }
  }, [dateRange, conversion]);

  // Debounced fetch to prevent too many requests
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchReport();
    }, 300);
    
    return () => {
      clearTimeout(timeoutId);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchReport]);

  const { tableData, tableKeys } = data;

  return (
    <Fragment>
      <br />
      <h2>SALES REPORT</h2>

      <RangePicker
        style={{ marginRight: 5, width: '25%' }}
        format="YYYY-MM-DD"
        value={[dateRange[0] ? moment(dateRange[0], 'YYYY-MM-DD') : null,
        dateRange[1] ? moment(dateRange[1], 'YYYY-MM-DD') : null]}
        onChange={(date, dateString) => setDateRange([...dateString])}
      />

      <Tooltip title="Conversion amount(for usd)" placement="right">
        <InputNumber
          style={{ marginRight: 5 }}
          min={1}
          value={conversion}
          onChange={val => setConversion(val ? val : 1)}
        />
      </Tooltip>

      <Tooltip title="Summary details for sales" placement="right">
        <Radio.Group
          onChange={e => setSummaryFormat(e.target.value)}
          style={{ marginRight: 5 }}
          defaultValue={summaryFormat} 
          size="medium">
          <Radio.Button value="none">None</Radio.Button>
          <Radio.Button value="daily">Daily</Radio.Button>
          <Radio.Button value="weekly">Weekly</Radio.Button>
          <Radio.Button value="monthly">Monthly</Radio.Button>
        </Radio.Group>
      </Tooltip>

      <Tooltip title="Export to CSV" placement="right">
        <Button
          onClick={exportSalesCSV}
          shape="circle"
          className="bl-cl"
          style={{ marginRight: 5 }}
          icon="file-excel"
        />
      </Tooltip>

      <Tooltip title="Refresh content" placement="right">
        <Button
          onClick={() => {
            // Clear cache for current params on manual refresh
            const cacheKey = getCacheKey();
            cacheRef.current.delete(cacheKey);
            fetchReport();
          }}
          shape="circle"
          className="bl-cl"
          style={{ marginRight: 5 }}
          icon="sync"
        />
      </Tooltip>

      <Spin spinning={spin} size="large">
        <TableSales
          tableData={tableData}
          tableKeys={tableKeys}
        />
      </Spin>
    </Fragment>
  )
}

export default Layout(SalesView);